import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Send, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, 
  XCircle, CheckCheck, User, Phone, Mail, FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Sidebar } from "./Dashboard";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const Referrals = () => {
  const location = useLocation();
  const { getAuthHeaders } = useAuth();
  
  const [sentReferrals, setSentReferrals] = useState([]);
  const [receivedReferrals, setReceivedReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [activeTab, setActiveTab] = useState("received");

  const fetchReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const [sentRes, receivedRes] = await Promise.all([
        axios.get(`${API}/referrals/sent`, {
          headers: getAuthHeaders(),
          withCredentials: true
        }),
        axios.get(`${API}/referrals/received`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
      ]);
      setSentReferrals(sentRes.data.referrals);
      setReceivedReferrals(receivedRes.data.referrals);
    } catch (error) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const handleStatusUpdate = async (referralId, status) => {
    try {
      await axios.put(`${API}/referrals/${referralId}?status=${status}`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(`Referral ${status}`);
      fetchReferrals();
      setSelectedReferral(null);
    } catch (error) {
      toast.error("Failed to update referral");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { class: "badge-pending", icon: Clock },
      accepted: { class: "badge-accepted", icon: CheckCircle },
      declined: { class: "badge-declined", icon: XCircle },
      completed: { class: "badge-completed", icon: CheckCheck }
    };
    return styles[status] || { class: "bg-slate-100 text-slate-700", icon: Clock };
  };

  const getUrgencyStyle = (urgency) => {
    const styles = {
      routine: "text-slate-600 bg-slate-100",
      urgent: "text-amber-700 bg-amber-50 font-medium",
      emergency: "text-red-700 bg-red-50 font-semibold"
    };
    return styles[urgency] || "text-slate-600 bg-slate-100";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const ReferralCard = ({ referral, type }) => {
    const status = getStatusBadge(referral.status);
    const StatusIcon = status.icon;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="referral-card cursor-pointer"
        onClick={() => setSelectedReferral({ ...referral, type })}
        data-testid={`referral-card-${referral.referral_id}`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarFallback className="bg-primary/10 text-primary">
                {type === "received" 
                  ? referral.from_physician?.name?.charAt(0) 
                  : referral.to_physician?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-slate-900">
                {type === "received" ? referral.from_physician?.name : referral.to_physician?.name}
              </p>
              <p className="text-sm text-slate-500">{formatDate(referral.created_at)}</p>
            </div>
          </div>
          <Badge className={status.class}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {referral.status}
          </Badge>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-slate-400" />
            <span className="text-slate-900 font-medium">{referral.patient_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600 text-sm truncate">{referral.reason}</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <Badge className={getUrgencyStyle(referral.urgency)}>
            {referral.urgency}
          </Badge>
          {type === "received" && referral.status === "pending" && (
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="outline"
                className="text-red-600 hover:bg-red-50 border-red-200"
                onClick={(e) => { e.stopPropagation(); handleStatusUpdate(referral.referral_id, "declined"); }}
              >
                Decline
              </Button>
              <Button 
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={(e) => { e.stopPropagation(); handleStatusUpdate(referral.referral_id, "accepted"); }}
              >
                Accept
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <Sidebar currentPath={location.pathname} />
      
      <main className="pt-20 lg:pt-8 p-4 lg:p-8" data-testid="referrals-main">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Referrals
            </h1>
            <p className="text-slate-600">Manage your patient referrals in one place</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <ArrowDownLeft className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-slate-500">Received</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{receivedReferrals.length}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpRight className="w-5 h-5 text-primary" />
                <span className="text-sm text-slate-500">Sent</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{sentReferrals.length}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <span className="text-sm text-slate-500">Pending</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {receivedReferrals.filter(r => r.status === 'pending').length}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-slate-500">Accepted</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {receivedReferrals.filter(r => r.status === 'accepted').length}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white border border-slate-200 p-1 mb-6">
              <TabsTrigger 
                value="received" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white"
                data-testid="tab-received"
              >
                <ArrowDownLeft className="w-4 h-4 mr-2" />
                Received ({receivedReferrals.length})
              </TabsTrigger>
              <TabsTrigger 
                value="sent"
                className="data-[state=active]:bg-primary data-[state=active]:text-white"
                data-testid="tab-sent"
              >
                <ArrowUpRight className="w-4 h-4 mr-2" />
                Sent ({sentReferrals.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="received">
              {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-slate-200 rounded-full"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-slate-200 rounded w-2/3 mb-2"></div>
                          <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : receivedReferrals.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {receivedReferrals.map((referral) => (
                    <ReferralCard key={referral.referral_id} referral={referral} type="received" />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                  <ArrowDownLeft className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No referrals received yet</h3>
                  <p className="text-slate-600">Complete your profile to start receiving referrals from the network</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="sent">
              {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-slate-200 rounded-full"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-slate-200 rounded w-2/3 mb-2"></div>
                          <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : sentReferrals.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sentReferrals.map((referral) => (
                    <ReferralCard key={referral.referral_id} referral={referral} type="sent" />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                  <ArrowUpRight className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No referrals sent yet</h3>
                  <p className="text-slate-600">Browse the directory to find physicians and send referrals</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Referral Detail Modal */}
      <Dialog open={!!selectedReferral} onOpenChange={() => setSelectedReferral(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Referral Details</DialogTitle>
          </DialogHeader>
          
          {selectedReferral && (
            <div className="space-y-6 mt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="w-12 h-12">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {selectedReferral.type === "received" 
                        ? selectedReferral.from_physician?.name?.charAt(0) 
                        : selectedReferral.to_physician?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900">
                      {selectedReferral.type === "received" 
                        ? `From: ${selectedReferral.from_physician?.name}` 
                        : `To: ${selectedReferral.to_physician?.name}`}
                    </p>
                    <p className="text-sm text-slate-500">{formatDate(selectedReferral.created_at)}</p>
                  </div>
                </div>
                <Badge className={getStatusBadge(selectedReferral.status).class}>
                  {selectedReferral.status}
                </Badge>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-slate-900">Patient Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Name</p>
                    <p className="font-medium text-slate-900">{selectedReferral.patient_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium text-slate-900">{selectedReferral.patient_phone}</p>
                  </div>
                  {selectedReferral.patient_email && (
                    <div className="col-span-2">
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium text-slate-900">{selectedReferral.patient_email}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-500">Reason for Referral</p>
                  <p className="text-slate-900">{selectedReferral.reason}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Urgency</p>
                    <Badge className={getUrgencyStyle(selectedReferral.urgency)}>
                      {selectedReferral.urgency}
                    </Badge>
                  </div>
                </div>
                {selectedReferral.notes && (
                  <div>
                    <p className="text-sm text-slate-500">Notes</p>
                    <p className="text-slate-900">{selectedReferral.notes}</p>
                  </div>
                )}
              </div>

              {selectedReferral.type === "received" && selectedReferral.status === "pending" && (
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button 
                    variant="outline"
                    className="flex-1 text-red-600 hover:bg-red-50 border-red-200"
                    onClick={() => handleStatusUpdate(selectedReferral.referral_id, "declined")}
                  >
                    Decline
                  </Button>
                  <Button 
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => handleStatusUpdate(selectedReferral.referral_id, "accepted")}
                  >
                    Accept Referral
                  </Button>
                </div>
              )}

              {selectedReferral.type === "received" && selectedReferral.status === "accepted" && (
                <div className="pt-4 border-t border-slate-200">
                  <Button 
                    className="w-full bg-primary hover:bg-primary/90 text-white"
                    onClick={() => handleStatusUpdate(selectedReferral.referral_id, "completed")}
                  >
                    Mark as Completed
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Referrals;
